const TIMEZONE = 'America/New_York'

export const reportsFunctions = {

  selectedClass: null,
  selectedYear: null,
  minimizedColumns: [],

  dateNow (date = null) {
    if (date === null) {
      return dayjs().tz(TIMEZONE) // only apply timezone when getting current time
    }
    return dayjs(date)  // Return with original timezone if any
  },

  disableTextSelection (divClass) {
    const div = document.querySelector(divClass)
    if (div) {
      div.style.webkitUserSelect = 'none'
      div.style.msUserSelect = 'none'
      div.style.MozUserSelect = 'none'
      div.style.userSelect = 'none'
    }
  },

  enableTableScrolling () {
    // Use setTimeout to ensure DOM is ready when called from Alpine.js
    setTimeout(() => {
      let container = document.querySelector('.report-table-container')
      let stickyItems = document.querySelectorAll('.sticky-left')

      if (container) {
        // Add scroll event for sticky positioning
        container.addEventListener('scroll', function () {
          let scrollPos = container.scrollLeft
          stickyItems.forEach(function (item) {
            item.style.transform = `translateX(${scrollPos}px)`
          })
        })

        // Add mouse drag functionality
        let pressed = false
        let startx
        let scrollLeft

        container.addEventListener('mousedown', (e) => {
          pressed = true
          startx = e.pageX - container.offsetLeft
          scrollLeft = container.scrollLeft
          container.style.cursor = 'grabbing'

          // Prevent text selection
          e.preventDefault()
          document.getSelection().removeAllRanges()
        })

        container.addEventListener('mousemove', (e) => {
          if (!pressed) return
          e.preventDefault()

          // Clear any text selection that might have occurred
          if (document.getSelection) {
            document.getSelection().removeAllRanges()
          }

          const walk = (e.pageX - container.offsetLeft) - startx
          container.scrollLeft = scrollLeft - walk
        })

        container.addEventListener('mouseup', () => {
          pressed = false
          container.style.cursor = 'grab'

          // Clear any remaining text selection
          if (document.getSelection) {
            document.getSelection().removeAllRanges()
          }
        })

        container.addEventListener('mouseleave', () => {
          pressed = false
          container.style.cursor = 'grab'

          // Clear any remaining text selection
          if (document.getSelection) {
            document.getSelection().removeAllRanges()
          }
        })
      }
    }, 1000) // Increased timeout even more
  },

  getActiveClassName () {
    return this.classes.find(Class => Class.id == this.activeClassId).name
  },

  getYearsFromRange () {
    return utils.getYearsMonthsFromRange(this.dateNow(this.ordersRange.leastDate.date), this.dateNow(this.ordersRange.farthestDate.date))
  },

  getMonthsFromRange () {
    return utils.getMonthsFromRange(this.dateNow(this.ordersRange.leastDate.date), this.dateNow(this.ordersRange.farthestDate.date))
  },

  getMonthlyCashflow (order, month) {
    if (!order.hasPaymentPlan) {
      const orderDate = this.dateNow(order.orderDate.date)
      const isSameMonth = orderDate.year() === month.year && (orderDate.month() + 1) === month.monthNumber

      return isSameMonth ? this.getPaidToDate(order.payments) : 0
    }

    // Handle orders with a payment plan.
    const { payments, adjustments } = this.getPaymentsForMonth(order, month)
    const paidPayments = payments.filter(payment => payment.paid_on)
    const paymentsTotal = paidPayments.reduce((total, payment) => total + parseFloat(payment.paid_amount || payment.amount), 0)
    const adjustmentsTotal = adjustments.reduce((total, adjustment) => total + parseFloat(adjustment.amount || adjustment.price), 0)
    return parseFloat(paymentsTotal + adjustmentsTotal)
  },

  getMonthlyTotalCashflow (order, month) {
    if (!order.hasPaymentPlan) {
      const orderDate = this.dateNow(order.orderDate.date)
      const isSameMonth = orderDate.year() === month.year && (orderDate.month() + 1) === month.monthNumber

      return isSameMonth ? this.getPaidToDate(order.payments) : 0
    }

    // Handle orders with a payment plan.
    const { payments, adjustments } = this.getPaymentsForMonth(order, month)
    const paymentsTotal = payments.reduce((total, payment) => total + parseFloat(payment.paid_amount || payment.amount), 0)
    const adjustmentsTotal = adjustments.reduce((total, adjustment) => total + parseFloat(adjustment.amount || adjustment.price), 0)
    return parseFloat(paymentsTotal + adjustmentsTotal)
  },

  monthIsInFuture (month) {
    const now = this.dateNow()
    return month.year > now.year() || (month.year === now.year() && month.monthNumber > now.month() + 1)
  },

  formatPrice (price, returnEmpty = true) {
    price = Number(price)
    let result = price % 1 === 0 ?
      price.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }) :
      price.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    return price === 0 && !returnEmpty ? '' : result
  },

  formatDate (date, format = 'MMM D, YYYY', returnEmpty = true) {
    if (!date) return returnEmpty ? '' : 'N/A'
    return window.dayjs(date).format(format)
  },

  hasOutdatedPayment (order) {
    return order.hasPaymentPlan && order.paymentSchedule.some(payment => !payment.paid_on && this.dateNow(payment.scheduled_on).isBefore(this.dateNow()))
  },

  getLastUnpaidPayment (order) {
    if (order.hasPaymentPlan) {
      const missedPayment = order.paymentSchedule.find(payment => !payment.paid_on && this.dateNow(payment.scheduled_on).isBefore(this.dateNow()))
      return missedPayment ? `${this.formatPrice(missedPayment.amount)} due on ${this.dateNow(missedPayment.scheduled_on).format('MMM DD, YYYY')}, ${missedPayment.attempts + 1} attempts` : ''
    }
    return ''
  },

  getBasePrice (order) {
    return Number(order.basePrice)
  },

  getPrice (order) {
    return order.hasPaymentPlan ? Number(order.basePrice) : Number(order.payInFullPrice)
  },

  getBaseDiscounts (order) {
    return Number(order.discountsAmount)
  },

  getBaseDiscountsBreakdown (order) {
    let result = []
    if (this.getBasePriceDiscount(order) > 0) {
      result.push(`Base price discount: ${this.formatPrice(this.getBasePriceDiscount(order))}`)
    }
    if (order.couponsAmount) {
      order.couponInfo.forEach(coupon => {
        result.push(`Coupon ${coupon.code}: ${this.formatPrice(coupon.amount)}`)
      })
    }
    return result.length ? result.join('<br>') : ''
  },

  getAdjustmentsTotal (order, types = 'all') {
    if (types === 'all') {
      types = ['order_adjustment']
    }
    return order.adjustments.reduce((total, adjustment) => {
      if (types.includes(adjustment.type)) {
        total += Number(adjustment.amount)
      }
      return Number(total)
    }, 0)
  },

  getAdjustmentsByType (order, types) {
    if (types === 'all') {
      types = ['order_adjustment']
    }
    return order.adjustments.filter(adjustment => types.includes(adjustment.type))
  },

  getAdjustmentsBreakdown (order, type = ['order_adjustment']) {
    const adjustments = this.getAdjustmentsByType(order, type)
    return adjustments ? adjustments.map(adjustment => `${adjustment.reason}: ${this.formatPrice(adjustment.amount)}`).join('<br>') : ''
  },

  getPurchasePrice (order) {
    return Number(order.purchasePrice) + Number(this.getAdjustmentsTotal(order))
  },

  getBasePriceDiscount (order) {
    return Number(order.basePriceDiscount)
  },

  getPurchasePriceBreakdown (order) {
    let result = []
    result = ['Base price: ' + this.formatPrice(order.basePrice)]
    if (this.getBasePriceDiscount(order) > 0) {
      result.push(`Pay in full discount: ${this.getBasePriceDiscount(order)}`)
    }
    if (order.couponsAmount > 0) {
      result.push(`Coupons: ${this.formatPrice(order.couponsAmount)}`)
    }
    if (order.adjustments.length) {
      result.push(this.getAdjustmentsBreakdown(order))
    }
    return result.length ? result.join('<br>') : ''
  },

  getBalance (order) {
    return this.getPurchasePrice(order) - this.getPaidToDate(order.payments)
  },

  getPaidToDateBreakdown (order) {
    const successfulPayments = this.getOrderPayments(order, { status: 'succeeded' })
    if (!successfulPayments.length) return ''

    return successfulPayments
      .map(payment => {
        const date = this.formatDate(payment.date, 'MMM DD YYYY')
        const amount = this.formatPrice(payment.amount)
        const source = payment.source || 'unknown'
        return `${date}: ${amount} (${source})`
      })
      .join('<br>')
  },

  getPaymentsToDateSum (order) {
    return order.paymentSchedule.reduce((total, payment) => {
      return payment.paid_on ? Number(total) + Number(payment.paid_amount) : total
    }, 0)
  },

  /**
   * Calculates the total paid amount to date for a given order by summing up its payments.
   *
   * @param {Object} source - The order object containing payment details.
   * @return {number} The total amount paid to date for the provided order.
   */
  getPaidToDate (source) {
    if (!source) return 0
    return source.reduce((total, payment) => {
      let amount = payment.source === 'credits' ? Number(payment.amount) * -1 : Number(payment.amount)
      return payment.status === 'succeeded' ? (Number(total) + Number(amount)) : total
    }, 0)
  },

  getPaymentsForMonth (order, month) {
    const date = this.dateNow(new Date(month.year, month.monthNumber - 1, 1))
    const payments = order.paymentSchedule ? order.paymentSchedule.filter((payment) => {
      // For paid payments, use the actual payment date to determine which month to show them in
      // For unpaid payments, use the scheduled date
      let paymentDate = payment.paid_on ? this.dateNow(payment.paid_on) : this.dateNow(payment.scheduled_on)
      return paymentDate.year() === date.year() && paymentDate.month() === date.month()
    }) : []
    const adjustments = order.adjustments ? order.adjustments.filter((adjustment) => {
      let adjustmentDate = this.dateNow(adjustment.date)
      return adjustmentDate.year() === date.year() && adjustmentDate.month() === date.month()
    }) : []
    payments.forEach(payment => payment.paymentType = 'payment')
    adjustments.forEach(adjustment => {
      adjustment.paymentType = 'adjustment'
      adjustment.paid_on = adjustment.date
      adjustment.scheduled_on = adjustment.date
      // Ensure amount property is available for calculations
      if (!adjustment.amount && adjustment.price) {
        adjustment.amount = adjustment.price
      }
    })
    const allPayments = [...payments, ...adjustments]
    allPayments.sort((a, b) => {
      // Sort by actual payment date if paid, otherwise by scheduled date
      const aDate = a.paid_on || a.scheduled_on
      const bDate = b.paid_on || b.scheduled_on
      if (aDate === null || aDate === undefined) return 1
      if (bDate === null || bDate === undefined) return -1
      if (aDate < bDate) return -1
      if (aDate > bDate) return 1
      return 0
    })
    allPayments.forEach((payment, index) => {
      if (payment.paid_on) payment.paymentKind = 'paid'
      if (payment.scheduled_on > this.dateNow().format('YYYY-MM-DD')) payment.paymentKind = 'future'
      if (payment?.type && payment.type === 'refund') payment.paymentKind = payment.type
      if (!payment.paid_on && (payment.last_failed_on || payment.scheduled_on < this.dateNow().format('YYYY-MM-DD'))) payment.paymentKind = 'failed'
    })

    return {
      payments: payments,
      adjustments: adjustments,
      allPayments: allPayments,
      hasMultiplePayments: payments.length > 1,
      hasFailedPayments: payments.some(payment => payment.paymentKind === 'failed')
    }
  },

  getOutstandingPayments () {
    return this.getOrderTotal() - this.getPaidToDate(fields.payments)
  },

  getOrderSubtotal () {
    return fields.orderItems.reduce((sum, item) => sum + item.purchasePrice, 0)
  },

  getOrderAdjustments () {
    return app.form.fields.orderItems.reduce((carry, item) => {
      if (!item.adjustments) return carry
      let adjustmentsFromItem = item.adjustments.map((adjustment) => {
        return {
          id: adjustment.id,
          amount: adjustment.amount,
          reason: `${adjustment.reason} (for ${item.productName})`,
          productId: adjustment.product_id,
          date: adjustment.date,
          type: adjustment.type,
        }
      })
      return carry.concat(adjustmentsFromItem)
    }, [])
  },

  getOrderAdjustmentsTotal () {
    return this.getOrderAdjustments().reduce((sum, adjustment) => sum + adjustment.amount, 0)
  },

  getOrderTotal () {
    return this.getOrderSubtotal() + this.getOrderAdjustmentsTotal()
  },

  getOrderFeesToDate (source) {
    return source.reduce((sum, item) => {
      return item.fee_amount ? sum + item.fee_amount : sum
    }, 0)
  },

  getOrderTotalCreditsUsed () {
    return this.orderHasCreditsUsage ? this.getOrderPaymentsBySource('credits', app.form.fields).reduce((sum, item) => {
      return item.amount ? sum + item.amount : sum
    }, 0) : 0
  },

  orderHasAdjustments () {
    return app.form.fields.orderItems.reduce((hasAdjustments, item) => {
      return hasAdjustments || (item.adjustments && item.adjustments.length > 0)
    }, false)
  },

  orderHasCreditsUsage () {
    return this.getOrderPaymentsBySource('credits', app.form.fields).length > 0
  },

  /**
   * Filters and retrieves payments from an order with various filtering options.
   * Payments are always sorted by date (oldest first).
   *
   * @param {Object} order - The order object containing payment details.
   * @param {Object} filters - Filtering options.
   * @param {string|Array} [filters.source] - Payment source(s) to filter by (e.g., 'stripe', ['stripe', 'venmo']).
   * @param {string|Array} [filters.status] - Payment status(es) to filter by (e.g., 'succeeded', ['succeeded', 'failed']).
   * @param {string|Array} [filters.type] - Payment type(s) to filter by (e.g., 'charge', ['charge', 'refund']).
   * @return {Array} A list of payments that match the filters, sorted by date. Returns an empty array if no payments match or if the order has no payments.
   */
  getOrderPayments (order, filters = {}) {
    if (!order.payments || !order.payments.length) {
      return []
    }
    let payments = [...order.payments]
    if (filters.source) {
      const sources = Array.isArray(filters.source) ? filters.source : [filters.source]
      payments = payments.filter(payment => sources.includes(payment.source))
    }
    if (filters.status) {
      const statuses = Array.isArray(filters.status) ? filters.status : [filters.status]
      payments = payments.filter(payment => statuses.includes(payment.status))
    }
    if (filters.type) {
      const types = Array.isArray(filters.type) ? filters.type : [filters.type]
      payments = payments.filter(payment => types.includes(payment.type))
    }

    return payments.sort((a, b) => new Date(a.date) - new Date(b.date))
  },

  /**
   * Filters and retrieves payments from an order based on the provided source.
   *
   * @param {string} source - The payment source to filter by.
   * @param {Object} order - The order object containing payment details (can be app.form.fields to get params from the form).
   * @return {Array} A list of payments that match the specified source. Returns an empty array if no payments match or if the order has no payments.
   */
  getOrderPaymentsBySource (source, order) {
    return this.getOrderPayments(order, { source })
  },

  isMonthRecognizable (month) {
    let isFutureDate = this.dateNow(new Date(month.year, month.month - 1, 1)).isAfter(this.dateNow())
    if (isFutureDate) {
      return false
    }
    let currentMonthIndex = this.incomeRecognitions.findIndex(incomeRecognition => incomeRecognition.month === month.month && incomeRecognition.year === month.year)
    if (currentMonthIndex === -1) {
      return true
    }
    if (currentMonthIndex === 0) {
      return true
    }
    return this.incomeRecognitions[currentMonthIndex - 1].recognized
  },

  getIncomeRecognitionByYear (year) {
    console.debug(Object.values(this.incomeRecognitions).filter(incomeRecognition => incomeRecognition.year === year) || [], `getIncomeRecognitionByYear: ${year}`)
    return Object.values(this.incomeRecognitions).filter(incomeRecognition => incomeRecognition.year === year) || []
  },

  checkIncomeRecognitionData () {
    let totalOrders = 0
    Object.values(this.orders).forEach(order => {
      if (order.incomeRecognition.length) {
        totalOrders++
      }
    })
    return totalOrders >= 10
  },

  loadIncomeRecognitions () {
    const incomeRecognitions = []

    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ]

    let firstEventDate = null
    let lastEventDate = null

    Object.values(this.orders).forEach(order => {
      if (!order?.incomeRecognition) {
        return
      }

      order.incomeRecognition.forEach(recognition => {
        if (!recognition?.events?.length) {
          return
        }

        recognition.events.forEach(event => {
          const eventDate = this.dateNow(event.date).toDate()
          const month = eventDate.getMonth() + 1
          const year = eventDate.getFullYear()
          const monthText = monthNames[month - 1]

          if (!firstEventDate || eventDate < firstEventDate) {
            firstEventDate = eventDate
          }
          if (!lastEventDate || eventDate > lastEventDate) {
            lastEventDate = eventDate
          }

          let incomeRecognition = incomeRecognitions.find(
            ir => ir.month === month && ir.year === year
          )

          if (!incomeRecognition) {
            incomeRecognition = {
              month,
              monthText,
              year,
              orders: [],
              total: 0,
              totalFees: 0,
              recognized: false
            }
            incomeRecognitions.push(incomeRecognition)
          }

          const orderExists = incomeRecognition.orders.some(
            existingOrder => existingOrder.id === order.orderId
          )

          if (!orderExists) {
            incomeRecognition.orders.push({
              orderId: order.orderId,
              customerId: order.customer.id,
              amount: Number(event.amount),
              amountFees: Number(event.amount_fees),
              classId: order.productId
            })
          }

          incomeRecognition.total += Number(event.amount)
          incomeRecognition.totalFees += Number(event.amount_fees)
        })
      })
    })

    incomeRecognitions.sort((a, b) => {
      if (a.year !== b.year) {
        return a.year - b.year
      }
      return a.month - b.month
    })

    for (let incomeRecognition of incomeRecognitions) {
      const matchingIncomeRecognition = this.incomeRecognitions.find(ir => ir.month === incomeRecognition.month && ir.year === incomeRecognition.year)
      if (matchingIncomeRecognition) {
        incomeRecognition.recognized = matchingIncomeRecognition.recognized
        incomeRecognition.orders = matchingIncomeRecognition.orders
        incomeRecognition.total = matchingIncomeRecognition.total
        incomeRecognition.totalFees = matchingIncomeRecognition.totalFees
      }
    }

    this.incomeRecognitions = incomeRecognitions
  },

  openOrder (order) {
    window.open(`/wp-admin/post.php?post=${order.orderId}&action=edit`, '_blank')
  },

  openStripeCustomer (order) {
    window.open(`https://dashboard.stripe.com/customers/${order.customer.stripe_id}`, '_blank')
  },

  openClass (classId) {
    window.open(`/wp-admin/post.php?post=${classId}&action=edit`, '_blank')
  },

  switchClass (classId) {
    const url = new URL(window.location.href)
    url.searchParams.delete('class_id')
    url.searchParams.set('class_id', classId)
    window.location.href = url.toString()
  },

  switchYear (year) {
    this.activeYear = year
    this.activeClassId = this.sortedClasses[0].id
    this.activeClass = this.activeClassId + '_' + year
    this.switchClass(this.activeClass)
  },

  switchClassFromPopup () {
    if (this.selectedClass) {
      this.switchClass(this.selectedClass)
    }
  },

  openOrdersByClass (classId) {
    window.open(`/wp-admin/edit.php?s&post_status=all&post_type=shop_order&product_filter=${classId}`, '_blank')
  },

  isColumnMinimized (columnName) {
    this.minimizedColumns = JSON.parse(localStorage.getItem('minimizedColumns')) || []
    return this.minimizedColumns[columnName] === true
  },

  toggleColumnVisibility (columnName) {
    const minimizedColumns = JSON.parse(localStorage.getItem('minimizedColumns')) || []
    minimizedColumns[columnName] = !minimizedColumns[columnName]
    localStorage.setItem('minimizedColumns', JSON.stringify(minimizedColumns))
    this.minimizedColumns = minimizedColumns
  },

  getStripeLink (payment) {
    return `https://dashboard.stripe.com/payments/${payment.charge_id}`
  }

}